import React, { useEffect, useRef, useState } from 'react';
import {
  Box, TextField, List, ListItem, ListItemAvatar, Avatar, ListItemText,
  Checkbox, FormControlLabel, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, Typography, Snackbar, CircularProgress
} from '@mui/material';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import GroupsIcon from '@mui/icons-material/Groups';
import ApiService from '../../modules/APIService.interceptors';
import ChatService from '../../services/chat.service';

const availableChannels = [
  { name: 'RECETAS', icon: <RestaurantMenuIcon /> },
  { name: 'LIVE', icon: <LiveTvIcon /> },
  { name: 'EJERCICIOS', icon: <FitnessCenterIcon /> },
  { name: 'COMUNIDAD', icon: <GroupsIcon /> }
];

const DEBOUNCE_MS = 350;
const MIN_CHARS = 2;

const AddModeratorDialog = ({ openDialog, handleCloseDialog, addModeratorToTable }) => {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [loader, setLoader] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const reqCounter = useRef(0);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!openDialog) return;
    if (search.trim().length >= MIN_CHARS) {
      triggerRemoteSearch(search.trim());
    } else {
      setUsers([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDialog]);

  useEffect(() => {
    if (!openDialog) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const term = search.trim();
      if (term.length >= MIN_CHARS) {
        triggerRemoteSearch(term);
      } else {
        setUsers([]);
      }
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, openDialog]);

  const triggerRemoteSearch = async (term) => {
    const myReqId = ++reqCounter.current;
    try {
      setLoader(true);
      const response = await ApiService.getUsers(
        50,      // limit
        0,       // offset
        'email',  // sort
        term     // query
      );

      if (myReqId !== reqCounter.current) return;

      if (!response?.error) {
        setUsers((response?.data?.users || []).reverse());
      } else {
        setUsers([]);
        setSnackbarMessage('Error al obtener usuarios.');
        setOpenSnackbar(true);
      }
    } catch (error) {
      if (myReqId !== reqCounter.current) return;
      console.error('Error al conectar con la API:', error);
      setUsers([]);
      setSnackbarMessage('Error al conectar con el servidor.');
      setOpenSnackbar(true);
    } finally {
      if (myReqId === reqCounter.current) setLoader(false);
    }
  };

  const handleSelectUser = (user) => setSelectedUser(user);

  const handleToggleChannel = (channel) => {
    setSelectedChannels((prev) =>
      prev.includes(channel) ? prev.filter((c) => c !== channel) : [...prev, channel]
    );
  };

  const handleAddModerator = async () => {
    if (!selectedUser) {
      setSnackbarMessage('Debés seleccionar un usuario.');
      setOpenSnackbar(true);
      return;
    }
    if (selectedChannels.length === 0) {
      setSnackbarMessage('Debés seleccionar al menos un canal.');
      setOpenSnackbar(true);
      return;
    }

    const payload = {
      arrOfChannelsProps: {
        userId: selectedUser.id,
        level: 'fullAccess',
        channelUid: selectedChannels
      },
      userId: selectedUser.id
    };

    try {
      setLoader(true);
      const response = await ChatService.addModerator(payload);
      if (response && !response.error) {
        setSnackbarMessage(`Moderador/a ${selectedUser.name} agregado correctamente.`);
        addModeratorToTable({
          id: selectedUser.id,
          name: `${selectedUser.name} ${selectedUser.lastname}`,
          email: selectedUser.email,
          avatar: selectedUser.avatar,
          channels: selectedChannels,
          level: 'fullAccess'
        });
      } else {
        setSnackbarMessage('Hubo un problema al agregar el moderador.');
      }
    } catch (error) {
      console.error('Error al enviar datos al servidor:', error);
      setSnackbarMessage('Error al conectar con el servidor.');
    } finally {
      setOpenSnackbar(true);
      setLoader(false);
      setSelectedUser(null);
      setSelectedChannels([]);
      setSearch('');
      handleCloseDialog();
    }
  };

  const handleCancel = () => {
    setSelectedUser(null);
    setSelectedChannels([]);
    setSearch('');
    setUsers([]);
    handleCloseDialog();
  };

  return (
    <Dialog open={openDialog} onClose={handleCancel} maxWidth="lg" fullWidth>
      <DialogTitle>Agregar Nuevo Moderador</DialogTitle>

      <DialogContent>
        <TextField
          label="Buscar usuaria por correo"
          variant="outlined"
          fullWidth
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2, mt: 2 }}
          helperText={`Escribí al menos ${MIN_CHARS} caracteres para buscar`}
        />

        {loader && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">Buscando usuarias…</Typography>
          </Box>
        )}

        {search.trim().length >= MIN_CHARS && !loader && (
          <>
            {users.length === 0 ? (
              <Typography variant="body2" sx={{ mb: 2 }}>
                No se encontraron resultados para “{search.trim()}”.
              </Typography>
            ) : (
              <List sx={{ maxHeight: 400, overflow: 'auto', mb: 2 }}>
                {users.map((user) => (
                  <ListItem
                    key={user.id}
                    onClick={() => handleSelectUser(user)}
                    selected={selectedUser?.id === user.id}
                    sx={{
                      border: selectedUser?.id === user.id ? '2px solid pink' : '1px solid #ccc',
                      borderRadius: 2,
                      mb: 1,
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f0f0f0' }
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar src={user?.avatar || 'https://via.placeholder.com/50'} />
                    </ListItemAvatar>
                    <ListItemText primary={`${user?.name} ${user?.lastname || ''}`} secondary={user.email} />
                  </ListItem>
                ))}
              </List>
            )}
          </>
        )}

        {selectedUser && (
          <Box sx={{ mb: 3, p: 2, border: '1px solid #ccc', borderRadius: 2, backgroundColor: '#f5f5f5' }}>
            <Typography variant="h6">Sirena seleccionada:</Typography>
            <Typography><strong>Nombre:</strong> {selectedUser?.name} {selectedUser?.lastname}</Typography>
            <Typography><strong>Correo:</strong> {selectedUser?.email}</Typography>
          </Box>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Seleccioná los canales:</Typography>
          {availableChannels.map(({ name, icon }) => (
            <FormControlLabel
              key={name}
              control={
                <Checkbox
                  checked={selectedChannels.includes(name)}
                  onChange={() => handleToggleChannel(name)}
                />
              }
              label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{icon}{name}</Box>}
              sx={{ mr: 2 }}
            />
          ))}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button color="error" onClick={handleCancel}>Cancelar</Button>
        <Button
          color="primary"
          variant="contained"
          onClick={handleAddModerator}
          disabled={!selectedUser || selectedChannels.length === 0 || loader}
        >
          {loader ? <CircularProgress size={20} sx={{ mr: 1 }} /> : 'Agregar'}
        </Button>
      </DialogActions>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        message={snackbarMessage}
        onClose={() => setOpenSnackbar(false)}
      />
    </Dialog>
  );
};

export default AddModeratorDialog;
